import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
} from "../../../Common";

import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useGenericDataPortForMyBag } from "../use-generic-data-port-for-mybag";

import { DataMutationMyBag_PromoCodeApply_QueryKey } from "./data-mutation--mybag-promo-code-apply-query-key-factory";

export interface DataMutationMyBagPromoCodeApplyConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<
    DataMutationMyBagPromoCodeApply_Result["data"]
  > {
  data: DataMutationMyBagPromoCodeApply_Result["data"];
  request: DataMutationMyBagPromoCodeApply_Result["variables"];
  status: DataMutationMyBagPromoCodeApply_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationMyBagPromoCodeApplyConsumerInterface;
const DataMutationMyBagPromoCodeApplyContext =
  React.createContext(defaultValue);

export interface DataMutationMyBagPromoCodeApply_ControllerInterface {
  mutate: DataMutationMyBagPromoCodeApply_Result["mutate"];
  mutateAsync: DataMutationMyBagPromoCodeApply_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationMyBagPromoCodeApply_ControllerInterface;
const DataMutationMyBagPromoCodeApply_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataMutationMyBagPromoCodeApply_Request {
  cartId: string;
  couponCode: string;
}
export type DataMutationMyBagPromoCodeApply_Result =
  ReactQuery.UseMutationResult<
    CheckoutModel_MyBag,
    unknown,
    DataMutationMyBagPromoCodeApply_Request,
    unknown
  >;

export type GraphqlResponse_MyBagPromoCodeApply = GraphqlWrappedData<
  "applyCouponToCart",
  { cart: CheckoutModel_MyBag }
>;

export interface DataMutationMyBagPromoCodeApplyProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationMyBagPromoCodeApplyProvider: React.FunctionComponent<
  DataMutationMyBagPromoCodeApplyProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [genericDataPortValues, genericDataPortController] =
    useGenericDataPortForMyBag();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutationMyBag_PromoCodeApply_QueryKey.mybagPromoCodeApply();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CheckoutModel_MyBag,
    DataMutationMyBagPromoCodeApply_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          applyCouponToCart( 
            input: {
              cart_id: "${request.cartId}"
              coupon_code: "${request.couponCode}"
            }
          ) {
            cart {
              ${CheckoutModel_MyBag_Graphql_Request}
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_MyBagPromoCodeApply>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      genericDataPortController.portError(data);

      const { data: gqlResponseData } = data || {};
      const { applyCouponToCart } = gqlResponseData || {};
      const { cart } = applyCouponToCart || {};

      if ((window as any).getMiniCart as any) {
        ((window as any).getMiniCart as any)((customerData: any) => {
          const cart = customerData.get("cart");
          customerData.reload(["cart"], true);
        });
      }

      genericDataPortController.portSuccess();

      return cart;
    },
    [initialState, genericDataPortController]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: genericDataPortController.useMutationOrQueryOnSuccess,
  });

  const ignoreMyCurrentState =
    genericDataPortController.ignoreMyCurrentStateFactory(mutation);
  const value: DataMutationMyBagPromoCodeApplyConsumerInterface = React.useMemo(
    () => ({
      data: mutation.data,
      request: mutation.variables,
      status: mutation.status,
      error: mutation.error,

      dataUpdatedAt: genericDataPortValues.dataUpdatedAt,
      errorUpdatedAt: genericDataPortValues.errorUpdatedAt,
      ignoreMyCurrentState: ignoreMyCurrentState,
    }),
    [
      mutation.data,
      mutation.variables,
      mutation.status,
      mutation.error,

      genericDataPortValues.dataUpdatedAt,
      genericDataPortValues.errorUpdatedAt,
      ignoreMyCurrentState,
    ]
  );
  const controllerValue: DataMutationMyBagPromoCodeApply_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationMyBagPromoCodeApplyContext.Provider value={value}>
      <DataMutationMyBagPromoCodeApply_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationMyBagPromoCodeApply_ControllerContext.Provider>
    </DataMutationMyBagPromoCodeApplyContext.Provider>
  );
};

export const useDataMutationMyBagPromoCodeApply = () => {
  const context = React.useContext(DataMutationMyBagPromoCodeApplyContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationMyBagPromoCodeApply must be used within a DataMutationMyBagPromoCodeApplyProvider"
    );
  }
  return context;
};

export const useDataMutationMyBagPromoCodeApply_asController = () => {
  const context = React.useContext(
    DataMutationMyBagPromoCodeApply_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationMyBagPromoCodeApply_asController must be used within a DataMutationMyBagPromoCodeApplyProvider"
    );
  }
  return context;
};
